/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import { useDashboardStats } from "../../store/useDashboardStats";
import { PrimaryButton } from "../buttons/PrimaryButton";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import { getWeekRange } from "../../services/dateService";
import Chart from "react-apexcharts";
import dayjs from "dayjs";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { monthlyToQuarterly } from "../../services/arrayService";

interface Props {
  periodProp?: "Daily" | "Weekly" | "LastTwelweMonths" | "Monthly" | "Yearly";
  weeklyRangeProp?: Date;
  monthRangeProp?: Date;
  yearRangeProp?: Date;
  className?: string;
}

export const VolumesGraphsChart = React.memo(
  ({
    periodProp,
    weeklyRangeProp,
    monthRangeProp,
    yearRangeProp,
    className,
  }: Props) => {
    /** Refs */
    const snapshotRef = useRef(null);
    /** Hooks */
    const { dropsStats, getDropsStats } = useDashboardStats();
    const [_, takeScreenshot] = useScreenshot();
    /** State */
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const [dropsPeriod, setDropsPeriod] = useState<
      "Weekly" | "Monthly" | "Quarterly" | "Yearly"
    >("Monthly");
    const [dropsPeriodYear, setDropsPeriodYear] = useState<number>(currentYear);
    const [weeklyRange, setWeeklyRange] = useState(new Date());

    /** Callbacks */
    const { startOfWeek, endOfWeek } = getWeekRange(weeklyRange);

    const getLastWeek = () =>
      setWeeklyRange(new Date(weeklyRange.setDate(weeklyRange.getDate() - 7)));
    const getNextWeek = () =>
      setWeeklyRange(new Date(weeklyRange.setDate(weeklyRange.getDate() + 7)));

    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        // Create a link to download the image
        const link = document.createElement("a");
        link.href = image;
        link.download = "volumes.png"; // Set the default file name
        link.click(); // Programmatically click the link to trigger download
      });
    };

    /** Effects */
    useEffect(() => {
      if (
        periodProp &&
        periodProp !== "Daily" &&
        periodProp !== "LastTwelweMonths"
      )
        setDropsPeriod(periodProp);
      if (weeklyRangeProp) setWeeklyRange(weeklyRangeProp);
      if (monthRangeProp) setWeeklyRange(monthRangeProp);
      if (yearRangeProp) setWeeklyRange(yearRangeProp);
    }, [periodProp, weeklyRangeProp, monthRangeProp, yearRangeProp]);
    useEffect(() => {
      getDropsStats(dropsPeriodYear, weeklyRange);
    }, [dropsPeriodYear, getDropsStats, weeklyRange]);

    return (
      <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl border-gray-border drop-shadow-none mb-1 sm:mb-10">
        <div className="flex flex-col sm:flex-row w-full items-start justify-between">
          {dropsPeriod === "Weekly" ? (
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-2 sm:mb-12">
              {/* Volumes of the week */}
              Volumes of the week
            </p>
          ) : null}
          {dropsPeriod === "Monthly" ? (
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-12">
              {/* Volumes of the last week */}
              Volumes of the current month
            </p>
          ) : null}
          {dropsPeriod === "Quarterly" ? (
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-12">
              {/* Volumes of the last week */}
              Volumes of the last quarters
            </p>
          ) : null}
          {dropsPeriod === "Yearly" ? (
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-12">
              {/* Volumes of the last week */}
              Volumes of the last year
            </p>
          ) : null}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end">
            {dropsPeriod === "Weekly" ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center mr-0 sm:mr-[50px] mb-2 sm:mb-0">
                <PrimaryButton size="sm" onClick={getLastWeek} label="Prev" />
                <div className="mx-1 sm:mx-4 font-satoshi font-medium my-1 sm:my-0">
                  {startOfWeek.toDateString()} - {endOfWeek.toDateString()}
                </div>
                <PrimaryButton
                  size="sm"
                  disabled={endOfWeek > new Date()}
                  onClick={getNextWeek}
                  label="Next"
                />
              </div>
            ) : null}
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {dropsPeriod}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                <MenuItem
                  disabled={dropsPeriod === "Weekly"}
                  onClick={() => setDropsPeriod("Weekly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Weekly
                </MenuItem>
                <MenuItem
                  disabled={dropsPeriod === "Monthly"}
                  onClick={() => setDropsPeriod("Monthly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Monthly
                </MenuItem>
                <MenuItem
                  disabled={dropsPeriod === "Quarterly"}
                  onClick={() => setDropsPeriod("Quarterly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Quarterly
                </MenuItem>
                <MenuItem
                  disabled={dropsPeriod === "Yearly"}
                  onClick={() => setDropsPeriod("Yearly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Yearly
                </MenuItem>
              </MenuList>
            </Menu>
            {dropsPeriod === "Monthly" || dropsPeriod === "Quarterly" ? (
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-3 sm:ml-4 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                    {dropsPeriodYear}
                    <ChevronDown className="ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  <MenuItem
                    onClick={() => setDropsPeriodYear(currentYear)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {currentYear}
                  </MenuItem>
                  <MenuItem
                    onClick={() => setDropsPeriodYear(currentYear - 1)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {currentYear - 1}
                  </MenuItem>
                  <MenuItem
                    onClick={() => setDropsPeriodYear(currentYear - 2)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {currentYear - 2}
                  </MenuItem>
                  <MenuItem
                    onClick={() => setDropsPeriodYear(currentYear - 3)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {currentYear - 3}
                  </MenuItem>
                </MenuList>
              </Menu>
            ) : null}
            <div className="ml-0 sm:ml-2" />
            <PrimaryButton
              size="sm"
              onClick={downloadScreenshot}
              label="Download image"
            />
          </div>
        </div>
        <div ref={snapshotRef}>
          {/* @ts-ignore */}
          {dropsPeriod === "Weekly" ? (
            Object.values(dropsStats?.dropsByWeek ?? {})?.length ? (
              <Chart
                {...chartConfig}
                // @ts-ignore
                options={{
                  ...chartConfig?.options,
                  xaxis: {
                    ...chartConfig?.options?.xaxis,
                    categories: Object.keys(dropsStats?.dropsByWeek ?? {}),
                  },
                  // options?.xaxis?.categories = []
                }}
                series={[
                  {
                    name: "Drops created",
                    data: Object.values(dropsStats?.dropsByWeek ?? {}),
                  },
                  {
                    name: "Statiegeld Drops created",
                    data: Object.values(dropsStats?.tomraDropsByWeek ?? {}),
                  },
                ]}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <p className="mb-[100px] font-satoshi font-bold text-2xl text-tailgrids-black">
                  No data found
                </p>
              </div>
            )
          ) : null}
          {/* @ts-ignore */}
          {dropsPeriod === "Monthly" ? (
            Object.values(dropsStats?.dropsByMonth ?? {})?.length ? (
              <Chart
                {...chartConfig}
                // @ts-ignore
                options={{
                  ...chartConfig?.options,
                  xaxis: {
                    ...chartConfig?.options?.xaxis,
                    categories: Object.keys(dropsStats?.dropsByMonth ?? {}),
                    labels: {
                      ...chartConfig?.options?.xaxis?.labels,
                      // @ts-ignore
                      formatter: function (value: number) {
                        let today = new Date();
                        today.setMonth(Number(value) - 1);
                        return dayjs(today).format("MMM");
                      },
                    },
                  },
                  // options?.xaxis?.categories = []
                }}
                series={[
                  {
                    name: "Drops created",
                    data: Object.values(dropsStats?.dropsByMonth ?? {}),
                  },
                  {
                    name: "Statiegeld Drops created",
                    data: Object.values(dropsStats?.tomraDropsByMonth ?? {}),
                  },
                ]}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <p className="mb-[100px] font-satoshi font-bold text-2xl text-tailgrids-black">
                  No data found
                </p>
              </div>
            )
          ) : null}
          {dropsPeriod === "Quarterly" ? (
            Object.values(dropsStats?.dropsByMonth ?? {})?.length ||
            Object.values(dropsStats?.tomraDropsByMonth ?? {})?.length ? (
              // @ts-ignore
              <Chart
                {...chartConfig}
                // @ts-ignore
                options={{
                  ...chartConfig?.options,
                  xaxis: {
                    ...chartConfig?.options?.xaxis,
                    categories: ["Q1", "Q2", "Q3", "Q4"],
                  },
                  // options?.xaxis?.categories = []
                }}
                series={[
                  {
                    name: "Drops created",
                    data: monthlyToQuarterly(dropsStats?.dropsByMonth ?? {}),
                  },
                  {
                    name: "Statiegeld Drops created",
                    data: monthlyToQuarterly(
                      dropsStats?.tomraDropsByMonth ?? {}
                    ),
                  },
                ]}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <p className="mb-[100px] font-satoshi font-bold text-2xl text-tailgrids-black">
                  No data found
                </p>
              </div>
            )
          ) : null}
          {/* @ts-ignore */}
          {dropsPeriod === "Yearly" ? (
            Object.values(dropsStats?.dropsByYear ?? {}) ? (
              <Chart
                {...chartConfig}
                // @ts-ignore
                options={{
                  ...chartConfig?.options,
                  xaxis: {
                    ...chartConfig?.options?.xaxis,
                    categories: Object.keys(dropsStats?.dropsByYear ?? {}),
                  },
                  // options?.xaxis?.categories = []
                }}
                series={[
                  {
                    name: "Drops created",
                    data: Object.values(dropsStats?.dropsByYear ?? {}),
                  },
                  {
                    name: "Statiegeld Drops created",
                    data: Object.values(dropsStats?.tomraDropsByYear ?? {}),
                  },
                ]}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <p className="mb-[100px] font-satoshi font-bold text-2xl text-tailgrids-black">
                  No data found
                </p>
              </div>
            )
          ) : null}
        </div>
      </div>
    );
  }
);

const chartConfig = {
  type: "bar",
  width: "100%",
  height: 240,
  options: {
    chart: {
      toolbar: {
        show: false,
      },
    },
    title: {
      show: "",
    },
    dataLabels: {
      enabled: false,
    },
    colors: ["#194128", "#377D51"],
    plotOptions: {
      bar: {
        columnWidth: 12,
        borderRadius: 2,
      },
    },
    xaxis: {
      axisTicks: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      labels: {
        style: {
          colors: "#64748B",
          fontSize: "14px",
          fontFamily: "Satoshi",
          fontWeight: 500,
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#64748B",
          fontSize: "14px",
          fontFamily: "Satoshi",
          fontWeight: 500,
        },
      },
    },
    grid: {
      show: true,
      borderColor: "#dddddd",
      strokeDashArray: 5,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
      padding: {
        top: 5,
        right: 0,
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      theme: "dark",
    },
  },
};
